/**
 * React Query mutations for proposals
 *
 * Provides mutation hooks for create, approve, reject, etc. with automatic cache invalidation
 */

import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import type { ProposalAPIError } from '../core/errors';
import { createProposalClient } from '../core/ProposalClient';
import type {
  BulkMarkReadRequest,
  BulkMarkReadResponse,
  BulkRejectRequest,
  BulkRejectResponse,
  ClarifyingAnswer,
  CreateProposalRequest,
  ProposalDetail,
  ProposedAction,
  ReassignProposalRequest,
  RejectProposalRequest,
  UpdateClarifyingAnswersRequest,
  UpdatePayloadRequest,
} from '../core/types';
import { proposalQueryKeys } from './keys';

// ============================================================================
// Mutation Types
// ============================================================================

/**
 * Variables for approve/reject/reassign mutations
 */
interface ProposalIdVariables {
  proposalId: number;
}

interface ApproveVariables extends ProposalIdVariables {
  /** Optional array of action IDs to execute (for selective execution) */
  selectedActionIds?: number[];
  /** Optional array of action payloads for stale data detection */
  actions?: Array<{ id: number; payload: Record<string, unknown> }>;
}

interface RejectVariables extends ProposalIdVariables {
  request?: RejectProposalRequest;
}

interface ReassignVariables extends ProposalIdVariables {
  request: ReassignProposalRequest;
}

interface UpdatePayloadVariables extends ProposalIdVariables {
  actionId: number;
  request: UpdatePayloadRequest;
}

// ============================================================================
// Cache Update Helpers
// ============================================================================

/**
 * Update cache after a successful mutation that returns ProposalDetail
 */
function updateProposalCacheAfterMutation(
  queryClient: ReturnType<typeof useQueryClient>,
  orgId: number,
  proposalId: number,
  data: ProposalDetail
) {
  // Update detail cache with new data
  queryClient.setQueryData(proposalQueryKeys.detail(orgId, proposalId), data);

  // Invalidate list queries to reflect changes
  queryClient.invalidateQueries({
    queryKey: proposalQueryKeys.lists(orgId),
  });
}

/**
 * Update cache after a successful delete mutation
 */
function updateCacheAfterDelete(
  queryClient: ReturnType<typeof useQueryClient>,
  orgId: number,
  proposalId: number
) {
  // Remove from detail cache
  queryClient.removeQueries({
    queryKey: proposalQueryKeys.detail(orgId, proposalId),
  });

  // Invalidate list queries to remove deleted proposal
  queryClient.invalidateQueries({
    queryKey: proposalQueryKeys.lists(orgId),
  });
}

// ============================================================================
// Create Proposal
// ============================================================================

/**
 * Hook for creating a new proposal
 *
 * @param orgId - Organization ID
 * @param options - Additional mutation options
 * @returns Mutation hook
 *
 * @example
 * ```typescript
 * const createMutation = useCreateProposal(orgId);
 *
 * const handleCreate = async () => {
 *   const proposal = await createMutation.mutateAsync(request);
 *   console.log('Created:', proposal.id);
 * };
 * ```
 */
export function useCreateProposal(
  orgId: number,
  options?: Omit<
    UseMutationOptions<ProposalDetail, ProposalAPIError, CreateProposalRequest>,
    'mutationFn'
  >
) {
  const queryClient = useQueryClient();
  const client = createProposalClient(orgId);

  return useMutation<ProposalDetail, ProposalAPIError, CreateProposalRequest>({
    mutationFn: (request: CreateProposalRequest) => client.create(request),
    ...options,
    onSuccess: (data, variables, onMutateResult, mutationContext) => {
      // Update cache with new proposal
      updateProposalCacheAfterMutation(queryClient, orgId, data.id, data);

      // Call user's onSuccess if provided
      options?.onSuccess?.(data, variables, onMutateResult, mutationContext);
    },
  });
}

// ============================================================================
// Approve Proposal
// ============================================================================

/**
 * Hook for approving and executing a proposal
 *
 * @param orgId - Organization ID
 * @param options - Additional mutation options
 * @returns Mutation hook
 *
 * @example
 * ```typescript
 * const approveMutation = useApproveProposal(orgId);
 *
 * const handleApprove = async () => {
 *   const result = await approveMutation.mutateAsync({ proposalId: 123 });
 *   if (result.execution_result?.failed === 0) {
 *     console.log('All actions succeeded!');
 *   }
 * };
 * ```
 */
export function useApproveProposal(
  orgId: number,
  options?: Omit<
    UseMutationOptions<ProposalDetail, ProposalAPIError, ApproveVariables>,
    'mutationFn'
  >
) {
  const queryClient = useQueryClient();
  const client = createProposalClient(orgId);

  return useMutation<ProposalDetail, ProposalAPIError, ApproveVariables>({
    mutationFn: ({
      proposalId,
      selectedActionIds,
      actions,
    }: ApproveVariables) =>
      client.approve(proposalId, selectedActionIds, actions),
    ...options,
    onSuccess: (data, variables, onMutateResult, mutationContext) => {
      // Update cache after approval
      updateProposalCacheAfterMutation(
        queryClient,
        orgId,
        variables.proposalId,
        data
      );

      // A `create_pipeline` proposal creates the org's deal pipeline via a
      // REST-backed schema endpoint that the CRM WebSocket store does NOT
      // refresh. Without this invalidation the Deals page keeps its stale
      // (empty) `['pipelines']` cache — so after approving the pipeline
      // zero-state's proposal, the zero-state persists instead of revealing the
      // new board. Mirror the invalidation the settings slideouts already do
      // (CreatePipelineSlideout, StagesSlideout, useUpdatePipelineStages) so
      // both entry points behave the same.
      const createdPipeline = data.actions?.some(
        action => action.proposed_action_type_name === 'create_pipeline'
      );
      if (createdPipeline) {
        void queryClient.invalidateQueries({
          queryKey: ['pipelines', orgId],
        });
      }

      options?.onSuccess?.(data, variables, onMutateResult, mutationContext);
    },
  });
}

// ============================================================================
// Reject Proposal
// ============================================================================

/**
 * Hook for rejecting a proposal
 *
 * @param orgId - Organization ID
 * @param options - Additional mutation options
 * @returns Mutation hook
 *
 * @example
 * ```typescript
 * const rejectMutation = useRejectProposal(orgId);
 *
 * await rejectMutation.mutateAsync({
 *   proposalId: 123,
 *   request: { reason: 'Email needs revision' },
 * });
 * ```
 */
export function useRejectProposal(
  orgId: number,
  options?: Omit<
    UseMutationOptions<ProposalDetail, ProposalAPIError, RejectVariables>,
    'mutationFn'
  >
) {
  const queryClient = useQueryClient();
  const client = createProposalClient(orgId);

  return useMutation<ProposalDetail, ProposalAPIError, RejectVariables>({
    mutationFn: ({ proposalId, request }: RejectVariables) =>
      client.reject(proposalId, request),
    ...options,
    onSuccess: (data, variables, onMutateResult, mutationContext) => {
      // Update cache after rejection
      updateProposalCacheAfterMutation(
        queryClient,
        orgId,
        variables.proposalId,
        data
      );

      options?.onSuccess?.(data, variables, onMutateResult, mutationContext);
    },
  });
}

// ============================================================================
// Reopen Proposal
// ============================================================================

/**
 * Hook for reopening a dismissed proposal
 *
 * @param orgId - Organization ID
 * @param options - Additional mutation options
 * @returns Mutation hook
 *
 * @example
 * ```typescript
 * const reopenMutation = useReopenProposal(orgId);
 *
 * await reopenMutation.mutateAsync({ proposalId: 123 });
 * ```
 */
export function useReopenProposal(
  orgId: number,
  options?: Omit<
    UseMutationOptions<ProposalDetail, ProposalAPIError, ProposalIdVariables>,
    'mutationFn'
  >
) {
  const queryClient = useQueryClient();
  const client = createProposalClient(orgId);

  return useMutation<ProposalDetail, ProposalAPIError, ProposalIdVariables>({
    mutationFn: ({ proposalId }) => client.reopen(proposalId),
    ...options,
    onSuccess: (data, variables, onMutateResult, mutationContext) => {
      updateProposalCacheAfterMutation(
        queryClient,
        orgId,
        variables.proposalId,
        data
      );

      options?.onSuccess?.(data, variables, onMutateResult, mutationContext);
    },
  });
}

// ============================================================================
// Reassign Proposal
// ============================================================================

/**
 * Hook for reassigning a proposal to a different user
 *
 * @param orgId - Organization ID
 * @param options - Additional mutation options
 * @returns Mutation hook
 *
 * @example
 * ```typescript
 * const reassignMutation = useReassignProposal(orgId);
 *
 * await reassignMutation.mutateAsync({
 *   proposalId: 123,
 *   request: { new_assignee_user_id: 99 },
 * });
 * ```
 */
export function useReassignProposal(
  orgId: number,
  options?: Omit<
    UseMutationOptions<ProposalDetail, ProposalAPIError, ReassignVariables>,
    'mutationFn'
  >
) {
  const queryClient = useQueryClient();
  const client = createProposalClient(orgId);

  return useMutation<ProposalDetail, ProposalAPIError, ReassignVariables>({
    mutationFn: ({ proposalId, request }: ReassignVariables) =>
      client.reassign(proposalId, request),
    ...options,
    onSuccess: (data, variables, onMutateResult, mutationContext) => {
      // Update cache after reassignment
      updateProposalCacheAfterMutation(
        queryClient,
        orgId,
        variables.proposalId,
        data
      );

      options?.onSuccess?.(data, variables, onMutateResult, mutationContext);
    },
  });
}

// ============================================================================
// Update Action Payload
// ============================================================================

/**
 * Hook for updating an action's payload
 *
 * @param orgId - Organization ID
 * @param options - Additional mutation options
 * @returns Mutation hook
 *
 * @example
 * ```typescript
 * const updateMutation = useUpdateActionPayload(orgId);
 *
 * await updateMutation.mutateAsync({
 *   proposalId: 123,
 *   actionId: 456,
 *   request: {
 *     payload: {
 *       action_params: {
 *         to: ['updated@example.com'],
 *         subject: 'Updated',
 *         body: 'New content',
 *       },
 *     },
 *   },
 * });
 * ```
 */
export function useUpdateActionPayload(
  orgId: number,
  options?: Omit<
    UseMutationOptions<
      ProposedAction,
      ProposalAPIError,
      UpdatePayloadVariables
    >,
    'mutationFn'
  >
) {
  const queryClient = useQueryClient();
  const client = createProposalClient(orgId);

  return useMutation<ProposedAction, ProposalAPIError, UpdatePayloadVariables>({
    mutationFn: ({ proposalId, actionId, request }: UpdatePayloadVariables) =>
      client.updateActionPayload(proposalId, actionId, request),
    ...options,
    onSuccess: (data, variables, onMutateResult, mutationContext) => {
      // Invalidate detail cache to refetch full proposal with updated action
      queryClient.invalidateQueries({
        queryKey: proposalQueryKeys.detail(orgId, variables.proposalId),
      });
      // Invalidate lists to reflect potential proposal title changes
      queryClient.invalidateQueries({
        queryKey: proposalQueryKeys.lists(orgId),
      });

      options?.onSuccess?.(data, variables, onMutateResult, mutationContext);
    },
  });
}

// ============================================================================
// Delete Proposal
// ============================================================================

/**
 * Hook for deleting a proposal
 *
 * @param orgId - Organization ID
 * @param options - Additional mutation options
 * @returns Mutation hook
 *
 * @example
 * ```typescript
 * const deleteMutation = useDeleteProposal(orgId);
 *
 * await deleteMutation.mutateAsync({ proposalId: 123 });
 * ```
 */
export function useDeleteProposal(
  orgId: number,
  options?: Omit<
    UseMutationOptions<undefined, ProposalAPIError, ProposalIdVariables>,
    'mutationFn'
  >
) {
  const queryClient = useQueryClient();
  const client = createProposalClient(orgId);

  return useMutation<undefined, ProposalAPIError, ProposalIdVariables>({
    mutationFn: async ({ proposalId }: ProposalIdVariables) => {
      await client.delete(proposalId);
      return undefined;
    },
    ...options,
    onSuccess: (data, variables, onMutateResult, mutationContext) => {
      // Update cache after deletion
      updateCacheAfterDelete(queryClient, orgId, variables.proposalId);

      options?.onSuccess?.(data, variables, onMutateResult, mutationContext);
    },
  });
}

// ============================================================================
// Bulk Reject Proposals
// ============================================================================

/**
 * Hook for bulk rejecting proposals matching filter criteria
 *
 * @param orgId - Organization ID
 * @param options - Additional mutation options
 * @returns Mutation hook
 *
 * @example
 * ```typescript
 * const bulkRejectMutation = useBulkRejectProposals(orgId);
 *
 * // Reject all email proposals
 * const result = await bulkRejectMutation.mutateAsync({
 *   filter_tags__contains: 'email',
 *   reason: 'Clearing inbox',
 * });
 * console.log(`Rejected ${result.rejected_count} proposals`);
 * ```
 */
export function useBulkRejectProposals(
  orgId: number,
  options?: Omit<
    UseMutationOptions<BulkRejectResponse, ProposalAPIError, BulkRejectRequest>,
    'mutationFn'
  >
) {
  const queryClient = useQueryClient();
  const client = createProposalClient(orgId);

  return useMutation<BulkRejectResponse, ProposalAPIError, BulkRejectRequest>({
    mutationFn: (request: BulkRejectRequest) => client.bulkReject(request),
    ...options,
    onSuccess: (data, variables, onMutateResult, mutationContext) => {
      // Invalidate all list queries since bulk operations affect multiple items
      queryClient.invalidateQueries({
        queryKey: proposalQueryKeys.lists(orgId),
      });

      // Invalidate all detail queries since we don't know which proposals were affected
      queryClient.invalidateQueries({
        queryKey: proposalQueryKeys.details(orgId),
      });

      options?.onSuccess?.(data, variables, onMutateResult, mutationContext);
    },
  });
}

/**
 * Hook for marking all matching proposals as read
 *
 * @param orgId - Organization ID
 * @param options - Additional mutation options
 * @returns Mutation result with marked count
 *
 * @example
 * ```typescript
 * const bulkMarkReadMutation = useBulkMarkReadProposals(orgId);
 *
 * // Mark all email proposals as read
 * const result = await bulkMarkReadMutation.mutateAsync({
 *   filter_tags__contains: 'email',
 * });
 * console.log(`Marked ${result.marked_count} proposals as read`);
 * ```
 */
export function useBulkMarkReadProposals(
  orgId: number,
  options?: Omit<
    UseMutationOptions<
      BulkMarkReadResponse,
      ProposalAPIError,
      BulkMarkReadRequest
    >,
    'mutationFn'
  >
) {
  const queryClient = useQueryClient();
  const client = createProposalClient(orgId);

  return useMutation<
    BulkMarkReadResponse,
    ProposalAPIError,
    BulkMarkReadRequest
  >({
    mutationFn: (request: BulkMarkReadRequest) => client.markAllAsRead(request),
    ...options,
    onSuccess: (data, variables, onMutateResult, mutationContext) => {
      // Invalidate list queries since read status affects list display
      queryClient.invalidateQueries({
        queryKey: proposalQueryKeys.lists(orgId),
      });

      // Invalidate all detail queries too — bulk_mark_read affects an unknown
      // set of proposals, so any cached detail entry could now show a stale
      // `read_at` value.
      queryClient.invalidateQueries({
        queryKey: proposalQueryKeys.details(orgId),
      });

      options?.onSuccess?.(data, variables, onMutateResult, mutationContext);
    },
  });
}

// ============================================================================
// Update Clarifying Answers
// ============================================================================

interface UpdateClarifyingAnswersVariables {
  proposalId: number;
  answers: ClarifyingAnswer[];
  /** True marks the set as submitted (stamps answers_submitted_at server-side);
   * omitted/false on autosave, which clears the stamp. */
  submitted?: boolean;
}

/**
 * Hook for updating clarifying answers on a proposal
 *
 * Seeds the detail cache from the response body because `clarifying_questions`
 * is read-only server-side and cannot be re-fetched via a normal PATCH round-trip.
 *
 * @param orgId - Organization ID
 * @param options - Additional mutation options
 * @returns Mutation hook
 *
 * @example
 * ```typescript
 * const updateAnswers = useUpdateClarifyingAnswers(orgId);
 *
 * await updateAnswers.mutateAsync({
 *   proposalId: 123,
 *   answers: [{ questionId: 'q1', kind: 'option', optionIds: ['opt1'] }],
 * });
 * ```
 */
export function useUpdateClarifyingAnswers(
  orgId: number,
  options?: Omit<
    UseMutationOptions<
      ProposalDetail,
      ProposalAPIError,
      UpdateClarifyingAnswersVariables
    >,
    'mutationFn'
  >
) {
  const queryClient = useQueryClient();
  const client = createProposalClient(orgId);

  return useMutation<
    ProposalDetail,
    ProposalAPIError,
    UpdateClarifyingAnswersVariables
  >({
    mutationFn: ({
      proposalId,
      answers,
      submitted,
    }: UpdateClarifyingAnswersVariables) => {
      const request: UpdateClarifyingAnswersRequest = {
        answers,
        submitted: submitted ?? false,
      };
      return client.updateClarifyingAnswers(proposalId, request);
    },
    ...options,
    onSuccess: (data, variables, onMutateResult, mutationContext) => {
      // Seed the detail cache from the response — clarifying_questions is
      // read-only server-side so a re-fetch would not return the updated value.
      updateProposalCacheAfterMutation(
        queryClient,
        orgId,
        variables.proposalId,
        data
      );

      options?.onSuccess?.(data, variables, onMutateResult, mutationContext);
    },
  });
}
